/**
 * Technician App version resolver (v1 hardcoded  vs  v2 widget-driven).
 *
 * The parallel v2 app is OFF by default. The backend decides per
 * user/group/domain via fsa_interface_settings.app_v2_* AND now also turns v2
 * on automatically when a custom interface template is assigned to the
 * technician (engine follows the template). It reports the decision on
 * GET /fsa/technician/interface/meta/ as `app_v2_enabled`, together with the
 * assigned version id and its `published_revision` (used to detect changes).
 *
 * Safety / no-flicker design:
 *  - The last known decision is cached in localStorage, KEYED PER TECHNICIAN,
 *    so the correct shell mounts instantly on next launch (no v1 flash for v2
 *    techs, no v2 flash for v1 techs, and never another technician's cached
 *    interface in a shared/emulator browser).
 *  - Fail-safe default is v1: any error or missing data keeps the proven
 *    hardcoded app. v2 is only ever chosen on an explicit True from the server.
 */
import { ref } from 'vue'
import axios from '@/api/client'
import { previewInterfaceVersion } from '@/preview/techPreview'

/** Current technician id (for per-user cache keys). '' when unknown. */
function _userId() {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const u = JSON.parse(raw)
    return String(u?.id || '')
  } catch {
    return ''
  }
}

function _cacheKey() {
  return `fsa_app_version:${_userId()}` // 'v1' | 'v2'
}

/** Did THIS technician already have a cached version decision on this device? */
function _hadCache() {
  try {
    return localStorage.getItem(_cacheKey()) != null
  } catch {
    return false
  }
}

function _readCache() {
  try {
    const v = localStorage.getItem(_cacheKey())
    return v === 'v2' ? 'v2' : 'v1'
  } catch {
    return 'v1'
  }
}

function _writeCache(version) {
  try {
    localStorage.setItem(_cacheKey(), version === 'v2' ? 'v2' : 'v1')
  } catch {
    /* storage full — non-fatal */
  }
}

function _keyFor(uid) {
  return `fsa_app_version:${uid || _userId()}`
}

// Singleton reactive state shared across the app.
const version = ref(_readCache())
const resolved = ref(false)
// True once a resolve attempt finished, success OR failure. `resolved` stays
// false on failure so a later call retries, but the boot gate must not keep the
// splash up for a decision that already came back (as an error) — see
// TechnicianApp `contentReady`.
const settled = ref(false)
// True when a cached decision existed at boot → the shell can render instantly
// and revalidate in the background (no splash). False only on the very first
// launch on this device, where we briefly wait for the server.
const wasCached = ref(_hadCache())
// Assigned interface version id + its published revision — lets the layout
// composable skip a full re-fetch when nothing changed.
const versionId = ref(null)
const revision = ref(null)
let inflight = null

async function resolveVersion(force = false) {
  // Builder Live Preview forces a concrete interface version — the widget
  // shell must render it regardless of the technician's own v2 rollout flag.
  if (previewInterfaceVersion()) {
    version.value = 'v2'
    resolved.value = true
    settled.value = true
    console.log('[AppVersion] LIVE PREVIEW override -> v2')
    return version.value
  }
  if (resolved.value && !force) return version.value
  if (inflight) return inflight
  inflight = (async () => {
    try {
      // Single cheap check (no billing): version flag + assigned version id +
      // published revision. The full layout is fetched separately, and only
      // when the revision actually changed.
      const { data } = await axios.get('/fsa/technician/interface/meta/')
      const next = data && data.app_v2_enabled ? 'v2' : 'v1'
      version.value = next
      versionId.value = data?.version_id ?? null
      revision.value = data?.published_revision ?? null
      _writeCache(next)
      resolved.value = true
      console.log('[AppVersion] resolved technician app version =', next,
        'version_id=', versionId.value, 'rev=', revision.value)
    } catch (e) {
      // Keep cached/default (v1). Never break the app over a version check.
      console.warn('[AppVersion] resolve failed — keeping', version.value, e?.message)
    } finally {
      inflight = null
      settled.value = true
    }
    return version.value
  })()
  return inflight
}

/**
 * Authoritatively seed the version decision from the technician LOGIN response
 * (before the tech app shell mounts). The backend already resolved the exact
 * same decision the running app would (assignment/rollout), so priming it here
 * removes the first-launch v1->v2 (or v2->v1) jump: the correct shell mounts on
 * the very first frame and the later /interface/meta/ check just confirms it.
 *
 * @param {object} p  { app_v2_enabled, interface_version, interface_revision }
 * @param {string|number} [uid]  technician id (localStorage 'user' may not be
 *   written yet at the exact moment of login — pass it explicitly to be safe).
 */
export function primeAppVersion(p, uid) {
  try {
    if (!p) return
    const next = p.app_v2_enabled ? 'v2' : 'v1'
    version.value = next
    versionId.value = p.interface_version ?? null
    revision.value = p.interface_revision ?? null
    resolved.value = true
    settled.value = true
    wasCached.value = true // a decision now exists -> mount instantly, no splash
    // Persist so a full reload / next launch is cache-first with the SAME value.
    try { localStorage.setItem(_keyFor(uid), next === 'v2' ? 'v2' : 'v1') } catch { /* non-fatal */ }
    console.log('[AppVersion] primed from login =', next,
      'version_id=', versionId.value, 'rev=', revision.value)
  } catch (e) {
    console.warn('[AppVersion] prime failed:', e?.message)
  }
}

/** Clear THIS technician's cached decision (e.g. on logout / switch-view). */
export function clearAppVersionCache(uid) {
  try { localStorage.removeItem(_keyFor(uid)) } catch { /* non-fatal */ }
}

export function useAppVersion() {
  // Kick off resolution on first use; callers may also await resolveVersion().
  resolveVersion()
  return {
    version,        // ref: 'v1' | 'v2'
    resolved,       // ref: has the server answered successfully this session
    settled,        // ref: a resolve attempt finished (success or failure)
    wasCached,      // ref: a cached decision existed at boot (render instantly)
    versionId,      // ref: assigned interface version id (or null)
    revision,       // ref: published revision of the assigned version (or null)
    resolveVersion, // force refresh (e.g. after admin toggles rollout)
  }
}
